import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { getIcon, type IconName } from "@sylviq/ui-react-web/extensions/icons";
import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  ChevronDown,
  LifeBuoy,
  ListChecks,
  LogIn,
  Tag,
  X,
} from "lucide-react";
import { type ComponentType, useEffect, useRef, useState } from "react";
import { PRODUCTS } from "@/features/products/catalog";
import {
  MARKETING_LOGIN_PATH,
  MARKETING_NAV,
  MARKETING_SIGNUP_PATH,
} from "../marketing-nav";
import { BrandLockup } from "./BrandLockup";

export interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const NAV_ICONS: Record<
  string,
  ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>
> = {
  Pricing: Tag,
  "How it works": ListChecks,
  Documentation: BookOpen,
  Support: LifeBuoy,
};

const rowClass =
  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[var(--foreground)] transition hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)]";

/**
 * Logged-out navigation rail. It uses the same top-level items as the desktop
 * header, with Products as the only disclosure, and keeps the primary account
 * actions in the footer. The surface is solid so the page header cannot show
 * through the rail.
 */
export function NavDrawer({ open, onClose }: NavDrawerProps) {
  const [productsOpen, setProductsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current
      ?.querySelector<HTMLButtonElement>('button[aria-label="Close navigation menu"]')
      ?.focus();
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close navigation"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay)]"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        className="absolute inset-y-0 left-0 flex w-80 max-w-[85vw] flex-col border-r border-[var(--border)] bg-[var(--surface-solid)] shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-3">
          <BrandLockup className="h-10 w-10" />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            <X className="size-5" aria-hidden="true" />
          </Button>
        </div>

        <nav aria-label="Site sections" className="flex-1 overflow-y-auto p-3">
          <div className="grid gap-1">
            <button
              type="button"
              aria-expanded={productsOpen}
              aria-controls="nav-products"
              onClick={() => setProductsOpen((value) => !value)}
              className={`${rowClass} w-full justify-between font-medium`}
            >
              Products
              <ChevronDown
                aria-hidden="true"
                className={`size-4 shrink-0 text-[var(--muted-foreground)] transition-transform motion-reduce:transition-none ${productsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {productsOpen ? (
              <ul id="nav-products" className="grid list-none gap-1 p-0 pb-1">
                {PRODUCTS.map((product) => {
                  const Icon = getIcon(product.icon as IconName);
                  return (
                    <li key={product.slug}>
                      <Link
                        to="/products/$slug"
                        params={{ slug: product.slug }}
                        onClick={onClose}
                        className={`${rowClass} pl-5`}
                      >
                        <Icon
                          className="size-4 shrink-0 text-[var(--muted-foreground)]"
                          aria-hidden="true"
                        />
                        {product.name}
                      </Link>
                    </li>
                  );
                })}
                <li>
                  <Link
                    to="/products"
                    onClick={onClose}
                    className={`${rowClass} pl-5 font-medium`}
                  >
                    <span className="size-4 shrink-0" aria-hidden="true" />
                    All products
                  </Link>
                </li>
              </ul>
            ) : null}

            {MARKETING_NAV.map((item) => {
              const Icon = NAV_ICONS[item.label] ?? BookOpen;
              return item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={onClose}
                  className={rowClass}
                >
                  <Icon
                    className="size-4 shrink-0 text-[var(--muted-foreground)]"
                    aria-hidden="true"
                  />
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={onClose}
                  className={rowClass}
                >
                  <Icon
                    className="size-4 shrink-0 text-[var(--muted-foreground)]"
                    aria-hidden="true"
                  />
                  {item.label}
                </a>
              );
            })}
          </div>
        </nav>

        <div className="grid gap-2 border-t border-[var(--border)] p-4">
          <Button asChild variant="outline" className="w-full rounded-full">
            <Link to={MARKETING_LOGIN_PATH} onClick={onClose}>
              <LogIn className="size-4" aria-hidden="true" />
              Sign in
            </Link>
          </Button>
          <Button asChild className="w-full rounded-full">
            <Link to={MARKETING_SIGNUP_PATH} onClick={onClose}>
              Get started
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
